(() => {
  if (window.__engravingPreviewBound) return;
  window.__engravingPreviewBound = true;

  const SAMPLE = 'Personalizado';

  /**
   * @param {Element} section
   */
  const floatPanel = (section) => {
    const owned = document.querySelector(`[data-engraving-float][data-owner="${CSS.escape(section.id)}"]`);
    if (owned) return owned;
    const local = section.querySelector('[data-engraving-float]');
    if (local instanceof HTMLElement) local.dataset.owner = section.id;
    return local;
  };

  /**
   * @param {Element} section
   * @param {HTMLElement} trigger
   */
  const openFloat = (section, trigger) => {
    const panel = floatPanel(section);
    if (!(panel instanceof HTMLElement)) return;
    const main = section.querySelector('[data-engraving-input]');
    const floatInput = panel.querySelector('[data-engraving-float-input]');
    if (main instanceof HTMLInputElement && floatInput instanceof HTMLInputElement && floatInput.value !== main.value) {
      floatInput.value = main.value;
    }
    if (panel.parentElement !== document.body) document.body.appendChild(panel);
    panel.hidden = false;
    document.documentElement.classList.add('engraving-float-open');
    panel._returnFocus = trigger;
    if (floatInput instanceof HTMLInputElement) floatInput.focus();
  };

  /**
   * @param {Element | null} panel
   */
  const closeFloat = (panel) => {
    if (!(panel instanceof HTMLElement) || panel.hidden) return;
    panel.hidden = true;
    document.documentElement.classList.remove('engraving-float-open');
    const back = panel._returnFocus;
    if (back instanceof HTMLElement) back.focus();
  };

  /**
   * @param {Element} section
   */
  const isEngravingVariant = (section) => {
    const checked = section.querySelector('input[type="radio"][data-variant-id]:checked');
    return checked?.getAttribute('aria-label') === 'Com gravação';
  };

  /**
   * @param {Element} section
   */
  const applyVariantState = (section) => {
    const field = section.querySelector('[data-engraving-field]');
    const input = section.querySelector('[data-engraving-input]');
    if (!field || !input) return;

    const active = isEngravingVariant(section);
    field.style.display = active ? 'flex' : 'none';
    input.required = active;
    if (!active) {
      input.value = '';
      const panel = floatPanel(section);
      const floatInput = panel?.querySelector('[data-engraving-float-input]');
      if (floatInput instanceof HTMLInputElement) floatInput.value = '';
      closeFloat(panel);
    }

    const confirmation = section.querySelector('[data-engraving-confirmation]');
    const checkbox = section.querySelector('[data-engraving-confirm]');
    if (confirmation) confirmation.hidden = !active;
    if (checkbox instanceof HTMLInputElement) {
      checkbox.disabled = !active;
      checkbox.required = active;
      if (!active) checkbox.checked = false;
    }
  };

  /**
   * @param {HTMLElement} preview
   */
  const drawPath = (preview) => {
    const path = preview.querySelector('[data-engraving-path]');
    if (!(path instanceof SVGPathElement)) return null;

    const curvature = Math.min(100, Math.max(0, Number(preview.dataset.engravingCurvature) || 0));
    const sag = (curvature / 100) * 240;
    const baseline = 340;
    path.setAttribute('d', `M 50 ${baseline} Q 500 ${baseline - sag} 950 ${baseline}`);
    return path;
  };

  /**
   * @param {HTMLElement} preview
   * @param {string} value
   */
  const fitText = (preview, value) => {
    const text = preview.querySelector('[data-engraving-text]');
    const textPath = preview.querySelector('[data-engraving-textpath]');
    const svg = preview.querySelector('svg');
    const path = drawPath(preview);
    if (
      !(text instanceof SVGTextElement) ||
      !(textPath instanceof SVGTextPathElement) ||
      !(svg instanceof SVGSVGElement) ||
      !path
    ) return;

    textPath.textContent = value;

    const viewWidth = svg.viewBox.baseVal.width || 1000;
    const viewHeight = svg.viewBox.baseVal.height || 500;
    // Use the unrotated viewport and account for preserveAspectRatio="meet".
    const userToPx = Math.min(svg.clientWidth / viewWidth, svg.clientHeight / viewHeight);
    if (userToPx <= 0) return;

    const tracking = Number.parseFloat(getComputedStyle(preview).getPropertyValue('--engraving-tracking')) || 0;
    text.style.letterSpacing = `${tracking / userToPx}px`;

    const minSize = Number(preview.dataset.engravingMinFont) || 12;
    const maxSize = Number(preview.dataset.engravingMaxFont) || 64;
    // Font settings are screen pixels; SVG measurements are in viewBox units.
    let low = Math.min(minSize, maxSize) / userToPx;
    let high = Math.max(minSize, maxSize) / userToPx;
    const available = path.getTotalLength() * 0.94;
    const fits = () => {
      const length = textPath.getComputedTextLength();
      const bounds = text.getBBox();
      return length > 0 && length <= available && bounds.width <= viewWidth * 0.94 && bounds.height <= viewHeight * 0.94;
    };

    text.style.fontSize = `${low}px`;
    // On narrow images, allow long names to shrink further rather than overflow.
    if (!fits()) low = 0;

    let best = low;
    for (let step = 0; step < 14; step += 1) {
      const size = (low + high) / 2;
      text.style.fontSize = `${size}px`;
      if (fits()) {
        best = size;
        low = size;
      } else {
        high = size;
      }
    }

    text.style.fontSize = `${best}px`;
    // Center the visible glyphs before the wrapper rotates, independently of the baseline.
    const bounds = text.getBBox();
    const x = viewWidth / 2 - (bounds.x + bounds.width / 2);
    const y = viewHeight / 2 - (bounds.y + bounds.height / 2);
    text.setAttribute('transform', `translate(${x} ${y})`);
  };

  /**
   * @param {Element} section
   */
  const syncSection = (section) => {
    const previews = section.querySelectorAll('[data-engraving-preview]');
    if (!previews.length) return;

    const input = section.querySelector('[data-engraving-input]');
    const field = section.querySelector('[data-engraving-field]');
    const fieldVisible = Boolean(field) && field.style.display !== 'none';
    const typed = input instanceof HTMLInputElement ? input.value.trim() : '';
    const inEditor = Boolean(window.Shopify?.designMode);
    const active = fieldVisible || inEditor;
    const text = typed || (active ? SAMPLE : '');
    const show = text.length > 0 && active;

    section.querySelectorAll('[data-engraving-launch]').forEach((launch) => {
      if (launch instanceof HTMLElement) launch.hidden = !active;
    });

    previews.forEach((preview) => {
      if (!(preview instanceof HTMLElement)) return;
      if (!show) {
        preview.hidden = true;
        return;
      }
      preview.hidden = false;
      if (preview.getClientRects().length === 0) return;
      fitText(preview, text);
    });

    const count = section.querySelector('[data-engraving-count]');
    if (count && input instanceof HTMLInputElement) {
      const max = input.maxLength > 0 ? input.maxLength : 0;
      count.textContent = max > 0 ? `${input.value.length}/${max}` : `${input.value.length}`;
    }
  };

  const syncAll = () => {
    document.querySelectorAll('.shopify-section').forEach((section) => {
      if (!section.querySelector('[data-engraving-field], [data-engraving-preview]')) return;
      applyVariantState(section);
      syncSection(section);
    });
  };

  /**
   * @param {Element | null} section
   */
  const blocksPurchase = (section) => {
    const checkbox = section?.querySelector('[data-engraving-confirm]');
    if (!(checkbox instanceof HTMLInputElement) || checkbox.disabled) return false;
    return !checkbox.checked;
  };

  /**
   * @param {Element} section
   * @param {boolean} report
   */
  const showConfirmationError = (section, report) => {
    const error = section.querySelector('[data-engraving-error]');
    const checkbox = section.querySelector('[data-engraving-confirm]');
    if (error) error.hidden = false;
    if (report && checkbox instanceof HTMLInputElement) checkbox.reportValidity();
    checkbox?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  document.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-engraving-input]')) {
      const section = target.closest('.shopify-section');
      if (!section) return;
      const floatInput = floatPanel(section)?.querySelector('[data-engraving-float-input]');
      if (floatInput instanceof HTMLInputElement && floatInput.value !== target.value) floatInput.value = target.value;
      syncSection(section);
      return;
    }

    if (target.matches('[data-engraving-float-input]')) {
      const panel = target.closest('[data-engraving-float]');
      const section = panel instanceof HTMLElement ? document.getElementById(panel.dataset.owner || '') : null;
      const main = section?.querySelector('[data-engraving-input]');
      if (!(main instanceof HTMLInputElement) || !section) return;
      if (main.value !== target.value) main.value = target.value;
      syncSection(section);
    }
  });

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const launch = target.closest('[data-engraving-launch]');
      if (launch instanceof HTMLElement) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const section = launch.closest('.shopify-section');
        if (section) openFloat(section, launch);
        return;
      }

      const dismiss = target.closest('[data-engraving-float-close], [data-engraving-float-save]');
      if (dismiss) closeFloat(dismiss.closest('[data-engraving-float]'));
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      document.querySelectorAll('[data-engraving-float]').forEach((panel) => closeFloat(panel));
      return;
    }

    if (event.key === 'Enter' && event.target instanceof HTMLInputElement && event.target.matches('[data-engraving-float-input]')) {
      event.preventDefault();
      closeFloat(event.target.closest('[data-engraving-float]'));
    }
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    const section = target.closest('.shopify-section');
    if (!section) return;

    if (target.matches('[data-engraving-confirm]')) {
      const error = section.querySelector('[data-engraving-error]');
      if (error && target.checked) error.hidden = true;
      return;
    }

    if (target.matches('input[type="radio"][data-variant-id]')) {
      applyVariantState(section);
      syncSection(section);
    }
  });

  document.addEventListener(
    'click',
    (event) => {
      const sticky = event.target instanceof Element ? event.target.closest('.sticky-add-to-cart__button') : null;
      if (!sticky) return;
      const section = sticky.closest('.shopify-section');
      if (!section || !blocksPurchase(section)) return;
      event.preventDefault();
      event.stopPropagation();
      showConfirmationError(section, true);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const section = form.closest('.shopify-section');
      if (!section || !blocksPurchase(section)) return;
      event.preventDefault();
      event.stopPropagation();
      showConfirmationError(section, true);
    },
    true
  );

  document.addEventListener(
    'invalid',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !target.matches('[data-engraving-confirm]')) return;
      const section = target.closest('.shopify-section');
      if (section) showConfirmationError(section, false);
    },
    true
  );

  document.addEventListener('variant:update', () => {
    requestAnimationFrame(syncAll);
  });

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target instanceof Element ? event.target.closest('.shopify-section') || event.target : null;
    if (!(section instanceof Element)) return;
    if (!section.querySelector('[data-engraving-field], [data-engraving-preview]')) return;
    applyVariantState(section);
    syncSection(section);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll);
  } else {
    syncAll();
  }

  const refitVisiblePreviews = () => {
    document.querySelectorAll('.shopify-section').forEach((section) => {
      if (section.querySelector('[data-engraving-preview]')) syncSection(section);
    });
  };

  window.addEventListener('resize', refitVisiblePreviews);
  window.matchMedia('(min-width: 750px)').addEventListener('change', refitVisiblePreviews);

  if (document.fonts?.load) {
    document.fonts.load('400 64px "Galano Grotesque"').then(refitVisiblePreviews);
  }
})();
